import React, { useEffect, useState, useRef } from "react";
import Decimal from "decimal.js";

import { loan_term, total_rate_calculator } from "../../../calculations/loan";

import { LOANS_WITH_RATE_CALCULATOR } from "../../../constants/loan_types";
import { ARM_TERMS } from "../../../constants/loan_terms";
import ORIGINATION_FEE_PAID_BY from "../../../constants/origination_fee_paid_by";
import LOAN_PAYMENT_TYPES, {
  INTEREST_ONLY,
  PAYMENT_PERIODS
} from "../../../constants/loan_payment_type";
import LENDERS from "../../../constants/lenders";
import { getAllLenders, addCustomLender } from "../../../constants/custom_lenders";
import LOAN_TYPES, {
  REFINANCE_LOAN_TYPES
} from "../../../constants/loan_types";
import PROPERTY_TYPES, {
  HOA_PROPERTY_TYPES
} from "../../../constants/property_type";
import FREQUENCIES from "../../../constants/frequencies";
import { YES, YES_NO } from "../../../constants/yes_no";
import ComboBox from "../../fields/combo_box";
import { WHOLESALE_LENDERS } from "../../../constants/lender_registry";

import LOAN_RATES from "../../../constants/loan_rates";
import NumberField from "../../fields/number_field";
import CurrencyField from "../../fields/currency_field";
import SelectField from "../../fields/select_field";
import TextField from "../../fields/text_field";
import PercentField from "../../fields/percent_field";

import ArmRateAdjustmentsTable from "./arm_rate_adjustments";
import PaymentAdjustmentTable from "./payment_adjustment_table";

import { useDocument } from "../../loan_tools/loan_tool";
import Section from "../../ui/section";
import SaveLoan from "../save_loan";
import { REFINANCE } from "../../../constants/document_types";

const SORTED_LOAN_RATES = Object.entries(LOAN_RATES)
  .sort(([a], [b]) => {
    return new Decimal(a).greaterThan(b) ? 1 : -1;
  })
  .map(([, value]) => value);

// Fields "Populate Data From Loan 1" copies on this page. Loan Type and Amortization
// come from the Loan Type page; Rate, Base Fee, and the Rate Calculator are excluded
// because the rate is selected manually per loan (and the base fee is tied to it).
const POPULATE_LE_FIELDS = [
  "origination_fee_paid_by",
  "display_fee",
  "brokered_loan",
  "brokered_to_lender",
  "lender",
  "lender_program",
  "property_type",
  "hoa1",
  "hoa1_frequency",
  "hoa2",
  "hoa2_frequency",
  "mello_roos_amount",
  "mello_roos_frequency",
  "payment_type",
  "interest_only_period",
  "balloon_payment",
  "balloon_payment_period",
  "prepayment_penalty",
  "prepayment_terms"
];

export default function EditLoanEstimate({
  onChangeLoan,
  showMainPage,
  loanIndex
}) {
  const [document, setDocument, { documentId, documentType }, { saveDocument }] = useDocument();
  const [loan, setLoan] = useState({ ...document.loans[loanIndex] });
  const {
    balloon_payment,
    balloon_payment_period,
    base_fee,
    brokered_loan,
    brokered_to_lender,
    display_fee,
    interest_only_period,
    lender,
    lender_program,
    origination_fee_paid_by,
    payment_type,
    prepayment_penalty,
    prepayment_terms,
    rate,
    type
  } = loan;
  const rate_calculator = { ...loan.rate_calculator };
  const isArm = ARM_TERMS.includes(loan.term);
  const isRefinance = documentType === REFINANCE;
  const callback = useRef();

  useEffect(() => {
    callback.current = () => {
      if (loan.is_modified) {
        if (isValidLoan()) {
          if (
            window.confirm(
              "You have unsaved data in Loan Form. Would you like to save it before you leave?"
            )
          ) {
            save();
          } else {
            //do nothing
          }
        } else {
          alert(
            "Not all necessary fields were entered. Therefore changes would be dismissed."
          );
        }
      }
    };
  }, [loan]);

  useEffect(() => {
    // do nothing

    return () => {
      callback.current();
    };
  }, []);

  function onChange(updatedState) {
    setLoan({ ...loan, ...updatedState, is_modified: true });
  }

  function onChangeRateCalculator(updatedState) {
    const rateCalculator = {
      ...rate_calculator,
      ...updatedState
    };
    const updatedRate = total_rate_calculator(rateCalculator);
    setLoan({
      ...loan,
      rate_calculator: rateCalculator,
      rate: updatedRate,
      is_modified: true
    });
  }

  function populateFromLoanOne() {
    if (
      window.confirm(
        "This will replace the fields on this page with Loan 1's values, continue?"
      )
    ) {
      const source = document.loans[0];
      const picked = POPULATE_LE_FIELDS.reduce((acc, field) => {
        acc[field] = source[field];
        return acc;
      }, {});
      setLoan({ ...loan, ...picked, is_modified: true });
    }
  }

  // From Loan 1: push this loan's shared fields (POPULATE_LE_FIELDS — everything
  // except Rate and Base Fee / Credit, which stay per loan) to every other loan
  // in one click, then return to the loan list. Commits Loan 1's current edits too.
  function copyToAllLoans() {
    if (
      !window.confirm(
        "Copy these fields to ALL loans? Rate and Base Fee / Credit stay per loan."
      )
    ) {
      return;
    }
    const picked = POPULATE_LE_FIELDS.reduce((acc, field) => {
      acc[field] = loan[field];
      return acc;
    }, {});
    const loans = document.loans.map((existing, index) =>
      index === loanIndex
        ? { ...existing, ...loan }
        : { ...existing, ...picked, is_modified: true }
    );
    setDocument({ ...document, loans });
    // We just committed Loan 1 via setDocument above; clear the local modified
    // flag so the unmount "save this loan" callback doesn't re-save stale state
    // over the copy we just wrote to the other loans.
    loan.is_modified = false;
    showMainPage();
  }

  // Do all loans currently share the same setup (the copied fields)? Live
  // indicator: flips to "No" the moment any loan diverges on a shared field.
  const allLoansSameSetup =
    (document.loans || []).length < 2 ||
    document.loans.every(other =>
      POPULATE_LE_FIELDS.every(
        field =>
          String(other[field] == null ? "" : other[field]) ===
          String(document.loans[0][field] == null ? "" : document.loans[0][field])
      )
    );

  function save() {
    addCustomLender(lender, LENDERS);
    if (brokered_to_lender) addCustomLender(brokered_to_lender, WHOLESALE_LENDERS);
    delete loan.is_modified;
    onChangeLoan(loan);
    saveDocument();
    showMainPage();
  }

  function validateNonEmptyString(value) {
    return value !== null && value !== undefined && value !== "";
  }

  function isValidLoan() {
    const validatedFields = [
      loan.origination_fee_paid_by,
      loan.display_fee,
      loan.brokered_loan,
      loan.lender,
      loan.rate,
      loan.base_fee,
      loan.property_type,
      loan.payment_type,
      loan.balloon_payment,
      loan.prepayment_penalty
    ];

    if (HOA_PROPERTY_TYPES.includes(loan.property_type)) {
      validatedFields.push(loan.hoa1, loan.hoa2, loan.mello_roos_amount);
    }
    if (loan.payment_type === INTEREST_ONLY) {
      validatedFields.push(loan.interest_only_period);
    }
    if (loan.balloon_payment === YES) {
      validatedFields.push(loan.balloon_payment_period);
    }
    if (ARM_TERMS.includes(loan.term)) {
      validatedFields.push(
        loan.arm_index_percent,
        loan.arm_margin,
        loan.arm_rate_floor
      );
    }

    return validatedFields.every(validateNonEmptyString);
  }

  return (
    <Section
      className="le-price-engine-edit-loan"
      name={`Edit Loan ${loanIndex + 1}`}
    >
      {loanIndex > 0 && (
        <div style={{ textAlign: "left", marginBottom: "12px" }}>
          <button className="button" onClick={populateFromLoanOne}>
            Populate Data From Loan 1
          </button>
        </div>
      )}
      <div className="flex">
        <div className="third-width">
          <SelectField
            name="Origination Fee Paid By"
            fieldName="origination_fee_paid_by"
            onChange={onChange}
            value={origination_fee_paid_by}
            selectOptions={ORIGINATION_FEE_PAID_BY}
          />
          <SelectField
            name="Display Fee"
            fieldName="display_fee"
            onChange={onChange}
            value={display_fee}
            selectOptions={YES_NO}
          />
          <SelectField
            name="Brokered Loan"
            fieldName="brokered_loan"
            onChange={onChange}
            value={brokered_loan}
            selectOptions={YES_NO}
          />
          <ComboBox
            id="lender"
            name="Lender"
            fieldName="lender"
            onChange={onChange}
            value={lender}
            options={getAllLenders(LENDERS)}
            placeholder="Select or type a lender…"
          />
          {brokered_loan === YES && (
            <ComboBox
              id="brokered_to_lender"
              name="Brokered To (internal)"
              fieldName="brokered_to_lender"
              onChange={onChange}
              value={brokered_to_lender}
              options={getAllLenders(WHOLESALE_LENDERS)}
              placeholder="Wholesale lender this loan is brokered to…"
              title="Internal only. The wholesale lender you are brokering this loan to. Used for management reporting — it is never shown to the borrower on the Summary Sheet or Loan Estimate."
            />
          )}
          <TextField
            name="Lender Program"
            fieldName="lender_program"
            onChange={onChange}
            value={lender_program}
          />

          <TextField
            readOnly
            name="Loan Type"
            value={isRefinance ? REFINANCE_LOAN_TYPES[type] : LOAN_TYPES[type]}
          />

          {LOANS_WITH_RATE_CALCULATOR.includes(loan.type) && (
            <div className="le-price-engine-rate-calculator">
              <div className="le-price-engine-title">Rate Calculator</div>
              <PercentField
                fieldName="base_rate"
                onChange={onChangeRateCalculator}
                name="Base Rate"
                value={rate_calculator.base_rate}
              />
              <PercentField
                fieldName="bank_statements"
                onChange={onChangeRateCalculator}
                name="Bank Statements"
                value={rate_calculator.bank_statements}
              />
              <PercentField
                fieldName="term_add_on"
                onChange={onChangeRateCalculator}
                name="Term add on"
                value={rate_calculator.term_add_on}
              />
              <PercentField
                fieldName="subordinate_second"
                onChange={onChangeRateCalculator}
                name="Subordinate 2nd"
                value={rate_calculator.subordinate_second}
              />
              <PercentField
                fieldName="full_doc"
                onChange={onChangeRateCalculator}
                name="Full Doc"
                value={rate_calculator.full_doc}
              />
              <PercentField
                fieldName="dti_add"
                onChange={onChangeRateCalculator}
                name="DTI Add"
                value={rate_calculator.dti_add}
              />
              <PercentField
                fieldName="asset_depletion"
                onChange={onChangeRateCalculator}
                name="Asset Depletion"
                value={rate_calculator.asset_depletion}
              />
              <PercentField
                fieldName="cash_out"
                onChange={onChangeRateCalculator}
                name="Cash Out"
                value={rate_calculator.cash_out}
              />
              <PercentField
                fieldName="loan_amount"
                onChange={onChangeRateCalculator}
                name="Loan Amount"
                value={rate_calculator.loan_amount}
              />
              <PercentField
                fieldName="other"
                onChange={onChangeRateCalculator}
                name="Other"
                value={rate_calculator.other}
              />
              <PercentField
                fieldName="other_two"
                onChange={onChangeRateCalculator}
                name="Other"
                value={rate_calculator.other_two}
              />
              <PercentField
                readOnly
                name="Rate"
                decimalPlaces={3}
                value={rate}
                title="LANIS Training Tip: Loan 1 should always be the Lowest Rate Possible and it is used in the Anti Stearing Disclosure. For other Loan Options, increase the Interest Rate for the appropriate Loan Type and the closing costs desired."
              />
            </div>
          )}

          {!LOANS_WITH_RATE_CALCULATOR.includes(loan.type) && (
            <PercentField
              id="rate"
              fieldName="rate"
              onChange={onChange}
              name="Rate"
              value={rate}
              roundTo={3}
              decimalPlaces={3}
              datalist={SORTED_LOAN_RATES}
              title="LANIS Training Tip: Loan 1 should always be the Lowest Rate Possible and it is used in the Anti Stearing Disclosure. For other Loan Options, increase the Interest Rate for the appropriate Loan Type and the closing costs desired."
            />
          )}

          <PercentField
            fieldName="base_fee"
            onChange={onChange}
            name="Base Fee / Credit"
            value={base_fee}
          />

          <NumberField readOnly name="Amortization" value={loan_term(loan)} />

          <SelectField
            onChange={onChange}
            fieldName="property_type"
            name="Property Type"
            value={loan.property_type}
            selectOptions={PROPERTY_TYPES}
          />

          {HOA_PROPERTY_TYPES.includes(loan.property_type) && (
            <>
              <CurrencyField
                onChange={onChange}
                fieldName="hoa1"
                name="HOA 1"
                value={loan.hoa1}
              />
              <SelectField
                onChange={onChange}
                fieldName="hoa1_frequency"
                name="HOA 1 Frequency"
                value={loan.hoa1_frequency}
                selectOptions={FREQUENCIES}
              />
              <CurrencyField
                onChange={onChange}
                fieldName="hoa2"
                name="HOA 2"
                value={loan.hoa2}
              />
              <SelectField
                onChange={onChange}
                fieldName="hoa2_frequency"
                name="HOA 2 Frequency"
                value={loan.hoa2_frequency}
                selectOptions={FREQUENCIES}
              />
              <CurrencyField
                onChange={onChange}
                fieldName="mello_roos_amount"
                name="Mello Roos / Other Fee"
                value={loan.mello_roos_amount}
              />
              <SelectField
                onChange={onChange}
                fieldName="mello_roos_frequency"
                name="Mello Roos / Other Fee Frequency"
                value={loan.mello_roos_frequency}
                selectOptions={FREQUENCIES}
              />
            </>
          )}

          <SelectField
            onChange={onChange}
            name="Payment Type"
            fieldName="payment_type"
            value={payment_type}
            selectOptions={LOAN_PAYMENT_TYPES}
          />

          {payment_type === INTEREST_ONLY && (
            <SelectField
              name="Interest only period"
              onChange={onChange}
              fieldName="interest_only_period"
              value={interest_only_period}
              selectOptions={PAYMENT_PERIODS}
            />
          )}

          <SelectField
            onChange={onChange}
            fieldName="balloon_payment"
            name="Balloon Payment"
            value={balloon_payment}
            selectOptions={YES_NO}
          />

          {balloon_payment === YES && (
            <SelectField
              name="Balloon Payment Period"
              onChange={onChange}
              fieldName="balloon_payment_period"
              value={balloon_payment_period}
              selectOptions={PAYMENT_PERIODS}
            />
          )}

          <SelectField
            onChange={onChange}
            name="Prepayment Penalty"
            fieldName="prepayment_penalty"
            value={prepayment_penalty}
            selectOptions={YES_NO}
          />

          {prepayment_penalty === YES && (
            <TextField
              name="Prepayment Terms"
              onChange={onChange}
              fieldName="prepayment_terms"
              value={prepayment_terms}
            />
          )}
        </div>

        {isArm && (
          <>
            <ArmRateAdjustmentsTable
              loan={loan}
              isEditLoan={true}
              onChange={onChange}
            />
            <PaymentAdjustmentTable loan={loan} isEditLoan={true} />
          </>
        )}
      </div>

      {loanIndex === 0 && document.loans.length > 1 && (
        <div className="copy-to-all-row">
          <span className="copy-to-all-label">
            Do ALL Loans have the same setup?{" "}
            <span className={allLoansSameSetup ? "cta-yes" : "cta-no"}>
              {allLoansSameSetup ? "Yes" : "No"}
            </span>
          </span>
          <button className="button" onClick={copyToAllLoans}>
            Copy to All Loans
          </button>
        </div>
      )}

      <button className="button" onClick={showMainPage}>
        Cancel
      </button>
      <button className="button" disabled={!isValidLoan()} onClick={save}>
        Save
      </button>
      {loanIndex + 1 === document.loans.length && (
        <SaveLoan
          className="button"
          name="Save and Continue"
          path={`/loan-tool/${documentId}/loan-estimate-price-engine-options`}
          onSave={() => {
            delete loan.is_modified;
            onChangeLoan(loan);
          }}
        />
      )}
    </Section>
  );
}
